import { Alert, Box, Button, IconButton } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { CVAutocomplete } from '../../components/CVAutocomplete';
import { FavoriteRowToggle } from '../../components/FavoriteRowToggle';
import { FavoritesRail } from '../../components/FavoritesRail';
import { WizardAccordion } from '../../components/WizardAccordion';
import { en } from '../../translations/en';

export interface AwardField {
  id: string;
  text: string;
}

interface AwardsSectionProps {
  awards: AwardField[];
  onChange: (next: AwardField[]) => void;
  cvAwards: string[];
  cvLoading: boolean;
  onAddToCV?: (text: string) => Promise<void> | void;
  linkError?: string | null;
  /**
   * When true, render the body without the outer WizardAccordion. The
   * edit-object Awards step already supplies a Card head with title and
   * subtitle, so the inner accordion would be a redundant nesting.
   */
  flat?: boolean;
}

export const AwardsSection = ({
  awards,
  onChange,
  cvAwards,
  cvLoading,
  onAddToCV,
  linkError,
  flat,
}: AwardsSectionProps) => {
  const updateAward = (index: number, value: string) => {
    onChange(
      awards.map((award, i) =>
        i === index ? { ...award, text: value } : award
      )
    );
  };

  const removeAward = (index: number) => {
    onChange(awards.filter((_, i) => i !== index));
  };

  const addAward = () => {
    onChange([...awards, { id: crypto.randomUUID(), text: '' }]);
  };

  const addAwardFromFavorite = (value: string) => {
    onChange([...awards, { id: crypto.randomUUID(), text: value }]);
  };

  // Note: do NOT clear the row after Add to CV — the artwork's award entry
  // is independent of the CV record. CVAutocomplete already disables the
  // chip once the value matches an option (after fetchCV refreshes), so
  // clearing here would just delete the user's award from the artwork.
  const handleAddToCVForRow = onAddToCV;

  const body = (
    <Box>
      <FavoritesRail
        type="awards"
        currentValue=""
        hideStar
        onPick={addAwardFromFavorite}
      />
      {linkError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {linkError}
        </Alert>
      )}
      {awards.map((award, index) => (
        <Box
          key={award.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
          }}
        >
          <CVAutocomplete
            label={`${en.wizard.provenance.awards.label} ${index + 1}`}
            value={award.text}
            options={cvAwards}
            onChange={(value) => updateAward(index, value)}
            onAddToCV={handleAddToCVForRow}
            placeholder={en.wizard.provenance.awards.placeholder}
            isLoading={cvLoading}
          />
          <FavoriteRowToggle
            type="awards"
            value={award.text}
            ariaLabel={`Save award ${index + 1} as favorite`}
          />
          <IconButton
            onClick={() => removeAward(index)}
            color="error"
            size="small"
            aria-label={`Remove award ${index + 1}`}
          >
            <Delete />
          </IconButton>
        </Box>
      ))}
      <Button
        startIcon={<Add />}
        onClick={addAward}
        variant="outlined"
        size="small"
      >
        {en.wizard.provenance.awards.addButton}
      </Button>
    </Box>
  );

  if (flat) {
    return <Box data-section-id="provenance-awards">{body}</Box>;
  }

  return (
    <WizardAccordion
      id="provenance-awards"
      title={en.wizard.provenance.awards.title}
      subtitle={en.wizard.provenance.awards.subtitle}
      defaultExpanded={true}
      privacyFieldPath="provenance.awards"
    >
      {body}
    </WizardAccordion>
  );
};
